import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Calendar, Clock, Users, MapPin, Loader2, CheckCircle, ArrowLeft,
  CreditCard, QrCode, Building, Wallet, Landmark, Car, CalendarCheck,
  ChevronLeft, ChevronRight, Info
} from 'lucide-react';
import { toast } from 'sonner';
import useStoreSubdomain from '@/hooks/useStoreSubdomain';

const MODES = { daily: 'Harian', hourly: 'Per Jam', session: 'Per Sesi', quantity: 'Per Unit' };
function fmt(n) { return `Rp ${(n || 0).toLocaleString('id-ID')}`; }
function fmtDate(iso) {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); } catch { return iso; }
}

export default function BookingDetail() {
  const { serviceId } = useParams();
  const subdomain = useStoreSubdomain();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availability, setAvailability] = useState(null);

  // Booking form state
  const [showForm, setShowForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [endDate, setEndDate] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [form, setForm] = useState(() => {
    let init = { customer_name: '', customer_email: '', customer_phone: '', notes: '', payment_method: 'qris' };
    const saved = localStorage.getItem('user');
    if (saved) { try { const u = JSON.parse(saved); if (u.role === 'customer') { init.customer_name = u.name || ''; init.customer_email = u.email || ''; } } catch {} }
    return init;
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Calendar state
  const [calMonth, setCalMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Autofill phone from customer profile
  useEffect(() => {
    const saved = localStorage.getItem('user');
    if (saved) { try { const u = JSON.parse(saved); if (u.role === 'customer' && localStorage.getItem('token')) { fetch('/api/customer/profile', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).then(r => r.ok ? r.json() : null).then(d => { if (d?.customer?.phone) setForm(f => ({ ...f, customer_phone: f.customer_phone || d.customer.phone })); }).catch(() => {}); } } catch {} }
  }, []);

  useEffect(() => {
    if (!subdomain || !serviceId) return;
    setLoading(true);
    fetch(`/api/store/${subdomain}/booking-services/${serviceId}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => setService(d))
      .catch(() => toast.error('Layanan tidak ditemukan'))
      .finally(() => setLoading(false));
  }, [subdomain, serviceId]);

  useEffect(() => {
    if (!subdomain || !serviceId || !calMonth) return;
    fetch(`/api/store/${subdomain}/booking-services/${serviceId}/availability?month=${calMonth}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setAvailability(d))
      .catch(() => {});
  }, [subdomain, serviceId, calMonth]);

  const handleBooking = async () => {
    if (!form.customer_name || !form.customer_email) return toast.error('Nama dan email wajib diisi');
    if (!selectedDate) return toast.error('Pilih tanggal booking');
    setSubmitting(true);
    try {
      const body = {
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        notes: form.notes,
        payment_method: form.payment_method,
        booking_date: selectedDate,
        quantity,
      };
      if (service.booking_mode === 'daily' && endDate) body.end_date = endDate;
      if (selectedSlot) body.slot_id = selectedSlot.id;

      const res = await fetch(`/api/store/${subdomain}/bookings/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...body, service_id: serviceId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Booking gagal');
      setResult(data);
      setShowForm(false);
    } catch (e) { toast.error(e.message); }
    setSubmitting(false);
  };

  // Month navigation
  const navMonth = (dir) => {
    const [y, m] = calMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setCalMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  // Generate calendar days
  const getCalDays = () => {
    const [y, m] = calMonth.split('-').map(Number);
    const first = new Date(y, m - 1, 1);
    const startDow = first.getDay();
    const daysInMonth = new Date(y, m, 0).getDate();
    const days = [];
    for (let i = 0; i < startDow; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  };

  const getDayAvail = (day) => {
    if (!day || !availability?.dates) return null;
    const dateStr = `${calMonth}-${String(day).padStart(2, '0')}`;
    return availability.dates.find(d => d.date === dateStr);
  };

  const todayStr = new Date().toISOString().split('T')[0];
  const monthNames = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
  const [calY, calM] = calMonth.split('-').map(Number);

  if (loading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin mr-2" /> Memuat...</div>;
  if (!service) return <div className="flex flex-col items-center justify-center min-h-screen text-slate-500"><p>Layanan tidak ditemukan</p></div>;

  // ============ RESULT PAGE ============
  if (result) {
    const isBankTransfer = result.bank_accounts?.length > 0;
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">{result.total_price > 0 ? 'Booking Dibuat!' : 'Booking Berhasil!'}</h1>
            <p className="text-slate-500 mt-2">{result.total_price > 0 ? 'Silakan lakukan pembayaran' : 'Booking Anda sudah dikonfirmasi'}</p>
          </div>

          <Card className="mb-4">
            <CardContent className="p-6 space-y-3">
              <h3 className="font-bold text-slate-800">📋 Detail Booking</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-slate-500">No. Booking:</span> <span className="font-mono font-bold">{result.booking_number}</span></div>
                <div><span className="text-slate-500">Layanan:</span> <b>{service.name}</b></div>
                <div><span className="text-slate-500">Tanggal:</span> {fmtDate(selectedDate)}</div>
                {service.booking_mode === 'daily' && endDate && <div><span className="text-slate-500">Sampai:</span> {fmtDate(endDate)}</div>}
                {selectedSlot && <div><span className="text-slate-500">Waktu:</span> {selectedSlot.time_start}-{selectedSlot.time_end}</div>}
                <div><span className="text-slate-500">Total:</span> <b className="text-green-600">{fmt(result.total_price)}</b></div>
              </div>
            </CardContent>
          </Card>

          <Card className="mb-4">
            <CardContent className="p-6 space-y-3">
              <h3 className="font-bold text-slate-800">👤 Data Pemesan</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-slate-500">Nama:</span> {form.customer_name}</div>
                <div><span className="text-slate-500">Email:</span> {form.customer_email}</div>
                {form.customer_phone && <div><span className="text-slate-500">Telepon:</span> {form.customer_phone}</div>}
              </div>
            </CardContent>
          </Card>

          {isBankTransfer && (
            <Card className="mb-4 border-blue-200 bg-blue-50">
              <CardContent className="p-6 space-y-3">
                <h3 className="font-bold text-blue-800">🏦 Transfer Pembayaran</h3>
                <div className="text-sm text-blue-700">Total: <b>{fmt(result.total_price)}</b></div>
                <div className="space-y-2">
                  {result.bank_accounts.map((acc, i) => (
                    <div key={i} className="bg-white rounded-lg p-3 border border-blue-200">
                      <div className="font-bold text-slate-800">{acc.bank_name}</div>
                      <div className="font-mono text-lg font-bold text-blue-600">{acc.account_number}</div>
                      <div className="text-sm text-slate-500">a.n. {acc.account_holder}</div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-blue-600">⚠️ Pastikan transfer sesuai nominal. Konfirmasi via WhatsApp setelah transfer.</p>
              </CardContent>
            </Card>
          )}

          {!isBankTransfer && result.payment_url && (
            <Card className="mb-4 border-amber-200 bg-amber-50">
              <CardContent className="p-6 space-y-3">
                <h3 className="font-bold text-amber-800">💳 Pembayaran</h3>
                <div className="text-sm">
                  <div><span className="text-amber-700">Metode:</span> {form.payment_method?.toUpperCase()}</div>
                  <div><span className="text-amber-700">Total:</span> <b>{fmt(result.total_price)}</b></div>
                </div>
                <a href={result.payment_url} target="_blank" rel="noreferrer">
                  <Button className="w-full bg-amber-600 hover:bg-amber-700"><CreditCard size={16} className="mr-2" /> Bayar Sekarang</Button>
                </a>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-6">
              <h3 className="font-bold text-slate-800 mb-3">📋 Selanjutnya</h3>
              <ol className="space-y-2 text-sm text-slate-600 list-decimal list-inside">
                {result.total_price > 0 ? (
                  isBankTransfer ? (
                    <>
                      <li>Transfer ke salah satu rekening di atas</li>
                      <li>Konfirmasi pembayaran via WhatsApp</li>
                      <li>Booking akan dikonfirmasi setelah pembayaran diterima</li>
                      <li>Detail booking akan dikirim via email & WhatsApp</li>
                    </>
                  ) : (
                    <>
                      <li>Selesaikan pembayaran melalui link di atas</li>
                      <li>Booking otomatis dikonfirmasi setelah pembayaran</li>
                      <li>Detail booking akan dikirim via email & WhatsApp</li>
                    </>
                  )
                ) : (
                  <>
                    <li>Booking Anda sudah dikonfirmasi</li>
                    <li>Detail booking telah dikirim ke email Anda</li>
                    <li>Datang sesuai jadwal yang telah ditentukan</li>
                  </>
                )}
              </ol>
            </CardContent>
          </Card>

          <div className="text-center mt-6">
            <Link to={`/store/${subdomain}`}><Button variant="outline"><ArrowLeft size={14} className="mr-1" /> Kembali ke Toko</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  // ============ MAIN DETAIL PAGE ============
  const mode = service.booking_mode;
  const hasSlots = availability?.slots?.length > 0;

  // Available slots for selected date
  const slotsForDate = availability?.slots?.filter(s => s.date === selectedDate && !s.is_blocked && s.available > 0) || [];

  // Compute total price based on mode
  const unitPrice = selectedSlot?.price_override ?? service.price ?? 0;
  const bookingDays = (() => {
    if (mode !== 'daily' || !selectedDate) return 1;
    if (!endDate) return 1;
    const d1 = new Date(selectedDate);
    const d2 = new Date(endDate);
    const diff = Math.ceil((d2 - d1) / (1000 * 60 * 60 * 24));
    return Math.max(1, diff);
  })();
  const totalPrice = mode === 'quantity' ? unitPrice * quantity : unitPrice * (mode === 'daily' ? bookingDays : 1);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Hero */}
      <div className="relative bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="max-w-5xl mx-auto px-4 py-12 md:py-16">
          <Link to={`/store/${subdomain}`} className="inline-flex items-center text-blue-200 hover:text-white text-sm mb-4">
            <ArrowLeft size={14} className="mr-1" /> Kembali ke Toko
          </Link>
          <div className="flex flex-col md:flex-row gap-8">
            <div className="flex-1">
              <Badge className="bg-white/20 text-white mb-3">{MODES[mode] || mode}</Badge>
              <h1 className="text-3xl md:text-4xl font-bold mb-3">{service.name}</h1>
              <p className="text-blue-100 mb-4 line-clamp-3">{service.description}</p>
              <div className="flex flex-wrap gap-4 text-sm text-blue-100">
                {service.location && <span className="flex items-center gap-1"><MapPin size={14} />{service.location}</span>}
                {service.operating_hours && <span className="flex items-center gap-1"><Clock size={14} />{service.operating_hours}</span>}
                <span className="flex items-center gap-1"><Users size={14} />Kapasitas: {service.capacity}</span>
              </div>
              <div className="mt-6">
                <span className="text-3xl font-bold">{fmt(service.price)}</span>
                <span className="text-blue-200 ml-2">{service.price_unit}</span>
              </div>
            </div>
            {service.images?.[0] && (
              <div className="w-full md:w-80 h-56 rounded-xl overflow-hidden shadow-lg">
                <img src={service.images[0]} alt={service.name} className="w-full h-full object-cover" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Info + Calendar */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            {service.description && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-3">Tentang Layanan</h3>
                  <p className="text-slate-600 whitespace-pre-wrap">{service.description}</p>
                </CardContent>
              </Card>
            )}

            {/* Calendar / Availability */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-4">Pilih Tanggal</h3>
                {/* Month nav */}
                <div className="flex items-center justify-between mb-4">
                  <Button variant="ghost" size="sm" onClick={() => navMonth(-1)}><ChevronLeft size={16} /></Button>
                  <span className="font-semibold">{monthNames[calM - 1]} {calY}</span>
                  <Button variant="ghost" size="sm" onClick={() => navMonth(1)}><ChevronRight size={16} /></Button>
                </div>
                {/* Day headers */}
                <div className="grid grid-cols-7 gap-1 mb-1">
                  {['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'].map(d => (
                    <div key={d} className="text-center text-xs font-medium text-slate-400 py-1">{d}</div>
                  ))}
                </div>
                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-1">
                  {getCalDays().map((day, i) => {
                    if (!day) return <div key={`e${i}`} />;
                    const dateStr = `${calMonth}-${String(day).padStart(2, '0')}`;
                    const isPast = dateStr < todayStr;
                    const dayAvail = getDayAvail(day);
                    const isBlocked = dayAvail?.is_blocked;
                    const isFull = dayAvail && dayAvail.available !== undefined && dayAvail.available <= 0;
                    const isSel = dateStr === selectedDate;
                    const canSelect = !isPast && !isBlocked && !isFull;

                    return (
                      <button
                        key={day}
                        disabled={!canSelect}
                        onClick={() => { setSelectedDate(dateStr); setSelectedSlot(null); }}
                        className={`
                          aspect-square rounded-lg text-sm font-medium transition-all relative
                          ${isSel ? 'bg-blue-600 text-white shadow-md' : ''}
                          ${!isSel && canSelect ? 'hover:bg-blue-50 text-slate-700' : ''}
                          ${isPast ? 'text-slate-300' : ''}
                          ${isBlocked ? 'bg-red-50 text-red-300 line-through' : ''}
                          ${isFull ? 'bg-slate-100 text-slate-300' : ''}
                          ${!isPast && !isBlocked && !isFull && dayAvail?.available > 0 ? 'ring-1 ring-green-300' : ''}
                        `}
                      >
                        {day}
                        {dayAvail?.price_override != null && !isSel && (
                          <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-orange-400" />
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="flex flex-wrap gap-3 mt-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded ring-1 ring-green-300" /> Tersedia</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-100" /> Penuh</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-50" /> Diblokir</span>
                </div>

                {/* Hourly/session slots for selected date */}
                {selectedDate && ['hourly', 'session'].includes(mode) && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="font-semibold text-sm mb-2">Pilih Waktu — {fmtDate(selectedDate)}</h4>
                    {slotsForDate.length === 0 ? (
                      <p className="text-sm text-slate-400">Tidak ada slot tersedia untuk tanggal ini.</p>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {slotsForDate.map(s => (
                          <button
                            key={s.id}
                            onClick={() => setSelectedSlot(s)}
                            className={`p-3 rounded-lg border text-sm text-left transition-all ${selectedSlot?.id === s.id ? 'border-blue-500 bg-blue-50' : 'hover:border-blue-300'}`}
                          >
                            <p className="font-medium">{s.time_start} - {s.time_end}</p>
                            <p className="text-xs text-slate-500">{s.available} tersedia {s.label && `· ${s.label}`}</p>
                            {s.price_override != null && <p className="text-xs font-medium text-orange-600">{fmt(s.price_override)}</p>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Terms */}
            {service.terms && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-3">Syarat & Ketentuan</h3>
                  <p className="text-slate-600 text-sm whitespace-pre-wrap">{service.terms}</p>
                </CardContent>
              </Card>
            )}
            {service.cancellation_policy && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-3">Kebijakan Pembatalan</h3>
                  <p className="text-slate-600 text-sm whitespace-pre-wrap">{service.cancellation_policy}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right sidebar: Booking action */}
          <div className="space-y-4">
            <Card className="sticky top-4">
              <CardContent className="p-6 space-y-4">
                <h3 className="font-bold text-lg">Pesan Sekarang</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Harga</span>
                    <span className="font-bold text-lg text-blue-600">{fmt(service.price)}<span className="text-xs text-slate-400 ml-1">{service.price_unit}</span></span>
                  </div>

                  {selectedDate ? (
                    <div className="bg-blue-50 rounded-lg p-3 text-sm space-y-1">
                      <p className="font-medium text-blue-800"><Calendar size={14} className="inline mr-1" />{fmtDate(selectedDate)}</p>
                      {selectedSlot && <p className="text-blue-600"><Clock size={14} className="inline mr-1" />{selectedSlot.time_start} - {selectedSlot.time_end}</p>}
                      {mode === 'daily' && endDate && bookingDays > 1 && (
                        <p className="text-blue-600">s/d {fmtDate(endDate)} ({bookingDays} hari)</p>
                      )}
                      {((mode === 'daily' && bookingDays > 1) || (mode === 'quantity' && quantity > 1)) && unitPrice > 0 && (
                        <p className="font-bold text-blue-700 pt-1 border-t border-blue-200 mt-1">
                          Total: {fmt(totalPrice)}
                          <span className="font-normal text-blue-500 ml-1 text-xs">
                            ({mode === 'daily' ? `${bookingDays} hari` : `${quantity} unit`} × {fmt(unitPrice)})
                          </span>
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-2">← Pilih tanggal di kalender</p>
                  )}

                  {mode === 'daily' && selectedDate && (
                    <div>
                      <Label className="text-xs">Tanggal Selesai (opsional)</Label>
                      <Input type="date" min={selectedDate} value={endDate} onChange={e => setEndDate(e.target.value)} />
                    </div>
                  )}

                  {mode === 'quantity' && (
                    <div>
                      <Label className="text-xs">Jumlah</Label>
                      <Input type="number" min={1} max={service.capacity} value={quantity} onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))} />
                    </div>
                  )}

                  <Button
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    size="lg"
                    disabled={!selectedDate || (['hourly', 'session'].includes(mode) && !selectedSlot)}
                    onClick={() => setShowForm(true)}
                  >
                    <CalendarCheck size={16} className="mr-2" /> Booking Sekarang
                  </Button>
                </div>

                {service.location_url && (
                  <a href={service.location_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                    <MapPin size={14} /> Lihat Lokasi
                  </a>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Booking form dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Formulir Booking</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Summary */}
            <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
              <p className="font-semibold">{service.name}</p>
              <p className="text-slate-500">{fmtDate(selectedDate)}{selectedSlot ? ` · ${selectedSlot.time_start}-${selectedSlot.time_end}` : ''}</p>
              {mode === 'daily' && endDate && <p className="text-slate-500">Sampai: {fmtDate(endDate)}{bookingDays > 1 ? ` (${bookingDays} hari)` : ''}</p>}
              {mode === 'quantity' && <p className="text-slate-500">Jumlah: {quantity}</p>}
              {((mode === 'daily' && bookingDays > 1) || (mode === 'quantity' && quantity > 1)) && unitPrice > 0 && (
                <p className="text-xs text-slate-400">{mode === 'daily' ? `${bookingDays} hari` : `${quantity} unit`} × {fmt(unitPrice)}</p>
              )}
              <p className="font-bold text-blue-600">{fmt(totalPrice)} {mode !== 'daily' || bookingDays <= 1 ? service.price_unit : ''}</p>
            </div>

            <div><Label>Nama Lengkap *</Label><Input value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))} placeholder="Nama lengkap" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.customer_email} onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))} placeholder="email@example.com" /></div>
            <div><Label>No. WhatsApp / Telepon</Label><Input value={form.customer_phone} onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))} placeholder="08xxxxxxxxxx" /></div>
            <div><Label>Catatan (opsional)</Label><Textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Catatan atau permintaan khusus" /></div>

            {service.price > 0 && (
              <div>
                <Label className="mb-2 block">Metode Pembayaran</Label>
                <RadioGroup value={form.payment_method} onValueChange={v => setForm(f => ({ ...f, payment_method: v }))} className="space-y-2">
                  <div className="flex items-center space-x-2 p-2 rounded-lg border hover:bg-slate-50">
                    <RadioGroupItem value="qris" id="pm-qris" /><Label htmlFor="pm-qris" className="flex items-center gap-2 cursor-pointer flex-1"><QrCode size={16} /> QRIS</Label>
                  </div>
                  <div className="flex items-center space-x-2 p-2 rounded-lg border hover:bg-slate-50">
                    <RadioGroupItem value="va" id="pm-va" /><Label htmlFor="pm-va" className="flex items-center gap-2 cursor-pointer flex-1"><Building size={16} /> Virtual Account</Label>
                  </div>
                  <div className="flex items-center space-x-2 p-2 rounded-lg border hover:bg-slate-50">
                    <RadioGroupItem value="ewallet" id="pm-ew" /><Label htmlFor="pm-ew" className="flex items-center gap-2 cursor-pointer flex-1"><Wallet size={16} /> E-Wallet</Label>
                  </div>
                  <div className="flex items-center space-x-2 p-2 rounded-lg border hover:bg-slate-50">
                    <RadioGroupItem value="bank_transfer" id="pm-bt" /><Label htmlFor="pm-bt" className="flex items-center gap-2 cursor-pointer flex-1"><Landmark size={16} /> Transfer Bank Manual</Label>
                  </div>
                </RadioGroup>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Batal</Button>
            <Button onClick={handleBooking} disabled={submitting}>
              {submitting && <Loader2 size={14} className="animate-spin mr-1" />}
              Konfirmasi Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
